import React, { useContext, useEffect } from 'react'
import { node } from 'prop-types'
import { StackView, Button, Icon, Text } from '@planningcenter/tapestry-react'
import { Link as RouterLink } from 'react-router-dom'
import { colors } from '../theme'
import { MenuContext, ActivePathContext } from './global_layout'
import { useLocation } from 'react-router-dom'
import store from '../../api/store'
import LinkButton from '../shared/link_button'

export const PrimaryNavLink = ({
  children,
  to,
  selected = false,
  ...props
}) => {
  return (
    <StackView
      to={to || undefined}
      as={to ? RouterLink : 'label'}
      tabIndex="0"
      paddingVertical={2}
      paddingHorizontal={4}
      role="button"
      css={{
        borderBottom: `1px solid ${colors['grey-5']}`,
        backgroundColor: '#fff',
        color: selected ? colors['primary'] : colors['grey-9'],
        fontWeight: 600,
        position: 'relative',
        transition: '0.2s backgroundColor ease',

        '&:focus-visible': {
          outline: `2px solid ${colors['primary']} !important`,
          outlineOffset: '-4px',
        },

        '&:hover': {
          backgroundColor: selected ? colors['grey-0'] : colors['grey-1'],
        },

        '&:before': {
          content: "''",
          display: 'block',
          height: '100%',
          width: '4px',
          background: selected ? colors['primary'] : 'transparent',
          position: 'absolute',
          left: 0,
          top: 0,
        },
      }}
      {...props}
    >
      {children}
    </StackView>
  )
}

export const SecondaryNavLink = ({ children, to, selected, ...props }) => {
  return (
    <StackView
      as={RouterLink}
      to={to}
      role="button"
      paddingLeft={5}
      paddingRight={2}
      paddingVertical={2}
      fontSize={4}
      css={{
        backgroundColor: selected ? colors['primary'] : '#fff',
        color: selected ? '#fff' : colors['grey-8'],
        fontWeight: 400,
        borderBottom: `1px solid ${colors['grey-5']}`,
        transition: '0.2s backgroundColor ease',

        '&:hover': {
          backgroundColor: !selected && colors['grey-1'],
        },

        '&:focus-visible': {
          outline: `2px solid ${colors['primary']} !important`,
          outlineOffset: '-4px',
        },
      }}
      {...props}
    >
      {children}
    </StackView>
  )
}

const Sidebar = ({ children, props }) => {
  const { mobileMenuOpen, setMobileMenuOpen } = useContext(MenuContext)
  const { setActiveOverviewPath, setActiveReferencePath } =
    useContext(ActivePathContext)
  const lastApp = store.get('lastApp') || 'people'
  const location = useLocation()

  const getActiveLinkClass = path => location.pathname.startsWith(`${path}`)

  useEffect(() => {
    location.pathname.startsWith('/apps')
      ? setActiveReferencePath(location.pathname)
      : setActiveOverviewPath(location.pathname)
  })

  return (
    <StackView
      axis="vertical"
      alignment="start"
      distribution="stretch"
      column={1}
      row={2}
      position="absolute"
      width="100vw"
      height="100vh"
      overflowX="hidden"
      overflowY="scroll"
      background="white"
      display={mobileMenuOpen ? 'block' : 'none'}
      zIndex={100}
      mediaQueries={{
        lg: {
          position: 'relative',
          width: 'auto',
          height: 'auto',
          minWidth: '320px',
          borderRight: `1px solid ${colors['grey-5']}`,
          minHeight: 'calc(100vh - 100px)',
        },
      }}
      {...props}
    >
      <StackView
        axis="horizontal"
        alignment="center"
        distribution="space-between"
        padding={4}
        mediaQueries={{
          lg: {
            visible: false,
          },
        }}
        style={{
          borderBottom: `1px solid ${colors['grey-5']}`,
          background: colors['grey-1'],
          position: 'sticky',
          top: 0,
          zIndex: 100,
        }}
      >
        <StackView axis="horizontal" spacing={1}>
          <LinkButton
            to="/overview/"
            theme={getActiveLinkClass('/overview') ? 'primary' : 'plain'}
            title="Overview"
          />

          <LinkButton
            to={`/apps/${lastApp}`}
            theme={getActiveLinkClass('/apps') ? 'primary' : 'plain'}
            title="Reference"
          />
        </StackView>
        <Button theme="plain" onClick={() => setMobileMenuOpen(false)}>
          <Icon name="general.x" size="sm" marginRight={1} />
          <Text weight={700}>Close</Text>
        </Button>
      </StackView>
      {children}
    </StackView>
  )
}

Sidebar.propTypes = {
  children: node,
}

export default Sidebar
